<template>
  <div class="services-table-partial-view">
    <div v-if="biobanksStore.waiting" class="status-text text-center">
      <output class="spinner-border" aria-live="polite" aria-label="Loading services"></output>
      <p class="mt-2">Loading services...</p>
    </div>

    <div v-else-if="filteredServices.length">
      <div class="d-flex mb-4 justify-content-between align-items-center">
        <div class="result-header">
          <h5>
            Services
            <span class="badge bg-secondary">{{ filteredServices.length }}</span>
          </h5>
        </div>
        <div class="pagination-controls">
          <nav aria-label="Services pagination">
            <ul class="pagination pagination-sm mb-0">
              <li :class="['page-item', { disabled: currentPage === 1 }]">
                <a class="page-link" href="#" @click.prevent="previousPage">Previous</a>
              </li>
              <li class="page-item disabled">
                <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
              </li>
              <li :class="['page-item', { disabled: currentPage === totalPages }]">
                <a class="page-link" href="#" @click.prevent="nextPage">Next</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <ServicesTable
        :services="paginatedServices"
        :columns="settingsStore.config.serviceTableColumns"
        :sort-field="sortField"
        :sort-order="sortOrder"
        @sort="handleSort"
        @row-click="handleServiceSelect"
      />

      <div class="d-flex justify-content-between align-items-center mt-4">
        <small class="text-muted"
          >Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ filteredServices.length }}</small
        >
        <nav aria-label="Services pagination bottom">
          <ul class="pagination pagination-sm mb-0">
            <li :class="['page-item', { disabled: currentPage === 1 }]">
              <a class="page-link" href="#" @click.prevent="previousPage">Previous</a>
            </li>
            <li class="page-item disabled">
              <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
            </li>
            <li :class="['page-item', { disabled: currentPage === totalPages }]">
              <a class="page-link" href="#" @click.prevent="nextPage">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div v-else class="status-text text-center">
      <h4>{{ noResultsText }}</h4>
      <p class="text-muted">Try adjusting your filters</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useBiobanksStore } from "../../stores/biobanksStore";
import { useFiltersStore } from "../../stores/filtersStore";
import { useSettingsStore } from "../../stores/settingsStore";
import { getSortValue } from "../../functions/serviceColumnValue";
import ServicesTable from "../tables/ServicesTable.vue";

const biobanksStore = useBiobanksStore();
const router = useRouter();
const filtersStore = useFiltersStore();
const settingsStore = useSettingsStore();

const currentPage = ref(1);
const pageSize = ref(20);

const sortField = ref(settingsStore.config.serviceTableColumns[0]?.column ?? "name");
const sortOrder = ref<"asc" | "desc">("asc");

const filteredServices = computed(() => {
  if (!biobanksStore.servicesForDisplay) {
    return [];
  }

  return biobanksStore.servicesForDisplay;
});

const sortedServices = computed(() => {
  const column = settingsStore.config.serviceTableColumns.find(
    (c) => c.column === sortField.value
  );
  if (!column) {
    return filteredServices.value;
  }

  return [...filteredServices.value].sort((a, b) => {
    const aValue = getSortValue(a, column);
    const bValue = getSortValue(b, column);

    if (typeof aValue === "string") {
      return sortOrder.value === "asc"
        ? aValue.localeCompare(String(bValue))
        : String(bValue).localeCompare(aValue);
    }

    return sortOrder.value === "asc"
      ? Number(aValue) - Number(bValue)
      : Number(bValue) - Number(aValue);
  });
});

const totalPages = computed(() => {
  const pages = Math.ceil(filteredServices.value.length / pageSize.value);
  return pages || 1;
});

const startIndex = computed(() => {
  return (currentPage.value - 1) * pageSize.value;
});

const endIndex = computed(() => {
  const end = currentPage.value * pageSize.value;
  return Math.min(end, filteredServices.value.length);
});

const paginatedServices = computed(() => {
  return sortedServices.value.slice(startIndex.value, endIndex.value);
});

function handleSort(column: string) {
  if (sortField.value === column) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
    return;
  }

  sortField.value = column;
  sortOrder.value = "asc";
}

const noResultsText = computed(() => {
  if (filtersStore.bookmarkWaitingForApplication) {
    return "Loading...";
  }

  return "No services were found";
});

function handleServiceSelect(service: any) {
  router.push({ name: "servicedetails", params: { id: service.id } });
}

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
}

function previousPage() {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
}

onMounted(() => {
  biobanksStore.getAllServices();
});

watch(
  () => filteredServices.value.length,
  () => {
    currentPage.value = 1;
  }
);
</script>

<style scoped>
.services-table-partial-view {
  width: 100%;
}

.status-text {
  padding: 2rem;
  color: #999;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.result-header h5 {
  margin: 0;
  color: #333;
}

.badge {
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  vertical-align: middle;
}

.pagination-controls {
  display: flex;
  gap: 1rem;
}

.pagination {
  margin: 0;
}

.page-link {
  cursor: pointer;
  color: #2196f3;
  border: 1px solid #dee2e6;
}

.page-link:hover {
  background-color: #f8f9fa;
  color: #1976d2;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
  color: #999;
  background-color: #f8f9fa;
}

.page-item.active .page-link {
  background-color: #2196f3;
  border-color: #2196f3;
  color: white;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  color: #2196f3;
}

@media (max-width: 768px) {
  .pagination-controls {
    display: none;
  }

  .d-flex {
    flex-direction: column;
    gap: 1rem;
  }

  .result-header {
    margin-bottom: 1rem;
  }
}
</style>
